/*
 * Signed-waiver RECORD — canon §6 signed-record rule (contract): "a stored
 * waiver record is the source of truth … the receipt is a stored record, not
 * a regenerated view", and a signed record can NEVER be edited client-side.
 *
 * Durable home: my-api's POST /me/tickets/:id/waiver (src/data/myApi.ts
 * submitWaiver) — the server record is what gates readiness. This module
 * keeps the participant-visible RECORD (doc titles+versions, declined
 * optional sections, signer, stamp) that the receipt and the read-only
 * re-open render from. Write-once per account per TICKET — store() refuses to
 * overwrite an existing record (fail-loud, no silent mutation).
 *
 * TICKET-SCOPED KEYS (PR#114 review P1-1, child-holder slice): records were
 * keyed per account per EVENT, which broke the slice's core scenario — a
 * guardian holding their OWN seat AND a child seat in the SAME event. The
 * guardian's own signature made the CHILD's waiver render as signed (submit
 * dead, child waiver_complete never true, QR never minted) — and the reverse
 * order leaked the child's record onto the guardian's own waiver. v2 keys
 * carry the ticketId; the legacy v1 event-keyed record remains readable ONLY
 * where it is unambiguous (caller-gated: self subject, single holding).
 *
 * // TODO(my-api): add GET /me/tickets/:id/waiver-record so re-open reads the
 * // server-held record; this device-local copy is the stopgap seam (same
 * // pattern as the wizard's profileStore extras). Cross-entity contract —
 * // escalates via boardroom, not invented here.
 */
import { loadSession } from '../../auth/session'

export interface WaiverRecord {
  eventId: string
  /** The my-api ticket the signature was recorded against (v2 records —
   *  child-holder slice). Absent on legacy v1 records and on the seamless
   *  local-only path when no ticket target resolved. */
  ticketId?: string
  /** Named party — the participant the waiver covers. */
  participant: string
  /** Guardian variant — signer is the guardian, not the participant. */
  guardian: boolean
  /** The signature exactly as typed. */
  signedBy: string
  /** Display stamp, e.g. `9 Aug 2026, 14:05`. */
  when: string
  signedAtIso: string
  docs: Array<{ title: string; version: string }>
  /** Optional section names declined at signing. */
  declined: string[]
}

/** `local` only ever appears without a session (test/dev — a real signature
 *  always has a session, since the expected name comes from the profile). */
const sub = (): string => (loadSession()?.claims?.sub as string | undefined) ?? 'local'

/** v2 — per account per event PER TICKET (P1-1). */
const ticketKey = (eventId: string, ticketId: string): string =>
  `mytrumin.waiverRecord.v2.${sub()}.${eventId}.${ticketId}`

/** LEGACY v1 — per account per event (ticket-ambiguous; read-only fallback). */
const legacyKey = (eventId: string): string => `mytrumin.waiverRecord.v1.${sub()}.${eventId}`

const parse = (raw: string | null): WaiverRecord | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as WaiverRecord
  } catch {
    return null
  }
}

/**
 * Load the signed record for an event (+ ticket where known).
 *  - `ticketId` given → the v2 ticket-scoped record wins; the legacy v1
 *    event-keyed record is honoured ONLY when `acceptLegacy` (the CALLER
 *    knows the holding topology: self subject + single holding — the one
 *    case a v1 record could only have meant this ticket) AND the legacy
 *    record is not a guardian record (a guardian record predating ticket
 *    scoping cannot be trusted to any ticket).
 *  - no `ticketId` → the legacy v1 read (pre-slice behaviour: seamless
 *    local-only flows and fixtures that never resolved a ticket target).
 */
export function loadWaiverRecord(
  eventId: string,
  opts: { ticketId?: string; acceptLegacy?: boolean } = {},
): WaiverRecord | null {
  if (opts.ticketId) {
    const v2 = parse(localStorage.getItem(ticketKey(eventId, opts.ticketId)))
    if (v2) return v2
    if (opts.acceptLegacy === false) return null
    const legacy = parse(localStorage.getItem(legacyKey(eventId)))
    return legacy && !legacy.guardian ? legacy : null
  }
  return parse(localStorage.getItem(legacyKey(eventId)))
}

/** Write-once. Throws rather than overwrite — a signed record is immutable
 *  client-side (canon §6 must-not). Records carrying a ticketId write the v2
 *  ticket-scoped key; ticketless records keep the legacy event key. */
export function storeWaiverRecord(record: WaiverRecord): void {
  const k = record.ticketId ? ticketKey(record.eventId, record.ticketId) : legacyKey(record.eventId)
  if (localStorage.getItem(k) !== null) {
    throw new Error('waiver record already exists — signed records cannot be modified')
  }
  localStorage.setItem(k, JSON.stringify(record))
}
